import React, { PureComponent } from 'react';
import { Dispatch, bindActionCreators } from 'redux';
import { connect } from 'react-redux';

import Fade from '@mui/material/Fade';

import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Tooltip from '@mui/material/Tooltip';
import CircularProgress from '@mui/material/CircularProgress';
import Chip from '@mui/material/Chip';
import { Send as SendIcon } from '@mui/icons-material';

import {
  Container, Header, HeaderTitleContainer, HeaderTitle, ButtonContainer, InfoContainer, ErrorLoadingText, NoItemsText, ActiveChip, ConsentRequiredChip, LinkOverride, IdText,
} from './styles';
import { IAppState } from '../../../state/reducers';
import {
  IProps, IState, IStateProps, IDispatchProps,
} from './interfaces';
import {
  getAllSettingsAction, deleteSettingsAction, resendPublishConsentEmailAction,
} from '../../../state/publishEndpoints';
import { createStatefulLocationDescriptor } from '../../../utils/routing';
import {
  IPublishSettings, ISettingsBase, SettingState, SettingType,
} from '../../../api/vault/publishEndpoints';
import { VaultButton, ConfirmDialog } from '../../Shared';
import { IConfirmDialogResult } from '../../Shared/ConfirmDialog';
import { Routes } from '../../../constants';

import NewProfileMenu from './NewProfileMenu/component';

const CONSENT_REQUIRED = 'Consent Required';
const RESEND_DIALOG_TITLE = 'Resend Confirmation Email';
const RESEND_DIALOG_MESSAGE = 'Are you sure you want to resend the confirmation email?';
const RESEND_TOOLTIP = 'Resend consent email';

// TODO: These should come from the API
const getChipForSettingState = (state: SettingState, onSendValidation: () => void) => {
  switch (state) {
    case SettingState.ConsentRequired:
      return (
        <ConsentRequiredChip
          label={CONSENT_REQUIRED}
          onDelete={onSendValidation}
          deleteIcon={(
            <Tooltip title={RESEND_TOOLTIP} aria-label={RESEND_TOOLTIP}>
              <SendIcon />
            </Tooltip>
          )}
        />
      );
    case SettingState.Active:
      return <ActiveChip label="Active" />;
    case SettingState.Inactive:
      return <Chip label="Inactive" />;
    default:
      return <Chip label={`Unknown (${state})`} />;
  }
};

enum DialogTypes {
  Delete,
  SendConsent
}

class PublishProfilesList extends PureComponent<IProps, IState> {
  state: Readonly<IState> = {
    deleteConfirmDialogOpen: false,
    sendConsentConfirmDialogOpen: false,
    selectedSettingsForDialog: null,
  };

  componentDidMount() {
    const { getAllSettingsActionConnect, loadState } = this.props;

    if (loadState && loadState.responseState) {
      return;
    }

    getAllSettingsActionConnect();
  }

  // eslint-disable-next-line class-methods-use-this
  private generateHeader() {
    return (
      <TableHead>
        <TableRow>
          <TableCell>ID</TableCell>
          <TableCell>Type</TableCell>
          <TableCell>Profile Name</TableCell>
          <TableCell>Description</TableCell>
          <TableCell>State</TableCell>
          <TableCell>Actions</TableCell>
        </TableRow>
      </TableHead>
    );
  }

  openDialog = (dialogType: DialogTypes, item: IPublishSettings<ISettingsBase>) => {
    this.setState({
      deleteConfirmDialogOpen: dialogType === DialogTypes.Delete,
      sendConsentConfirmDialogOpen: dialogType === DialogTypes.SendConsent,
      selectedSettingsForDialog: item,
    });
  };

  onDeleteDialogResult = (result: IConfirmDialogResult) => {
    this.setState({
      deleteConfirmDialogOpen: false,
      sendConsentConfirmDialogOpen: false,
    }, () => {
      const { selectedSettingsForDialog } = this.state;

      const { deleteSettingsActionConnect, getAllSettingsActionConnect } = this.props;

      if (result.confirmed) {
        deleteSettingsActionConnect(selectedSettingsForDialog._id);

        // Note - isWorking flag will be false once we get here, so for 1 second our buttons will not be disabled
        setTimeout(() => {
          getAllSettingsActionConnect();
        }, 1000);
      }
    });
  };

  onSendConsentDialogResult = (result: IConfirmDialogResult) => {
    this.setState({
      deleteConfirmDialogOpen: false,
      sendConsentConfirmDialogOpen: false,
    }, () => {
      if (result.confirmed) {
        const { selectedSettingsForDialog } = this.state;
        const { resendConsentActionConnect } = this.props;

        resendConsentActionConnect(selectedSettingsForDialog._id);
      }
    });
  };

  private generateBody() {
    const { isWorking, loadState } = this.props;

    const rowGenerator = (settings: IPublishSettings<ISettingsBase>[]) => {
      if (!settings) {
        return null;
      }

      return settings.map((item) => {
        let editLink = null;
        if (item.type === SettingType.YouTube) {
          editLink = createStatefulLocationDescriptor<IPublishSettings<any>>(Routes.PUBLISH_PROFILES_EDIT_YOUTUBE, item);
        } else if (item.type === SettingType.Twitter) {
          editLink = createStatefulLocationDescriptor<IPublishSettings<any>>(Routes.PUBLISH_PROFILES_EDIT_TWITTER, item);
        } else if (item.type === SettingType.LAN) {
          editLink = createStatefulLocationDescriptor<IPublishSettings<any>>(Routes.PUBLISH_PROFILES_EDIT_LAN, item);
        } else if (item.type === SettingType.Yahoo) {
          editLink = createStatefulLocationDescriptor<IPublishSettings<any>>(Routes.PUBLISH_PROFILES_EDIT_YAHOO, item);
        }

        return (
          <TableRow key={Math.random()}>
            <TableCell>
              <IdText>{ item._id }</IdText>
            </TableCell>
            <TableCell>
              { item.type }
            </TableCell>
            <TableCell>
              { item.settings.profileName }
            </TableCell>
            <TableCell>
              { item.settings.description }
            </TableCell>
            <TableCell>
              { getChipForSettingState(item.state, () => this.openDialog(DialogTypes.SendConsent, item)) }
            </TableCell>
            <TableCell component="th" scope="row">
              <ButtonContainer>
                <LinkOverride to={editLink}>
                  <VaultButton disabled={isWorking || editLink == null}>
                    Edit
                  </VaultButton>
                </LinkOverride>
                <VaultButton disabled={isWorking} onClick={(e: any) => this.openDialog(DialogTypes.Delete, item)}>Delete</VaultButton>
              </ButtonContainer>
            </TableCell>
          </TableRow>
        );
      });
    };

    return (
      <TableBody>
        { rowGenerator(loadState.responseState) }
      </TableBody>
    );
  }

  renderLoadError = () => {
    const { isWorking, getAllSettingsActionConnect } = this.props;

    return (
      <Fade in>
        <InfoContainer>
          <ErrorLoadingText>Unable to retrieve profiles</ErrorLoadingText>
          <VaultButton disabled={isWorking} onClick={(e) => getAllSettingsActionConnect()}>Reload</VaultButton>
        </InfoContainer>
      </Fade>
    );
  };

  renderNoItemsInfo = () => {
    const { isWorking, getAllSettingsActionConnect } = this.props;

    return (
      <Fade in>
        <InfoContainer>
          <NoItemsText>No publish profiles created</NoItemsText>
          <VaultButton disabled={isWorking} onClick={(e) => getAllSettingsActionConnect()}>Reload</VaultButton>
        </InfoContainer>
      </Fade>
    );
  };

  render() {
    const { loadState, isWorking } = this.props;
    const {
      deleteConfirmDialogOpen,
      sendConsentConfirmDialogOpen,
    } = this.state;

    // TODO: Rob - add API param to group settings by type as a response
    return (
      <Fade in>
        <Container>
          <Header>
            <HeaderTitleContainer
              data-auto="PublishProfilesPage"
            >
              <HeaderTitle>Publish Profiles</HeaderTitle>
            </HeaderTitleContainer>
            <ButtonContainer>
              { isWorking && <CircularProgress size={24} /> }
              <NewProfileMenu isDisabled={isWorking} />
            </ButtonContainer>
          </Header>
          <Table>
            { this.generateHeader() }
            { this.generateBody() }
          </Table>
          { loadState.error && this.renderLoadError() }
          { !loadState.isLoading && !loadState.error && loadState.responseState && loadState.responseState.length === 0 && this.renderNoItemsInfo() }
          <ConfirmDialog open={deleteConfirmDialogOpen} onResult={this.onDeleteDialogResult} />
          <ConfirmDialog
            open={sendConsentConfirmDialogOpen}
            onResult={this.onSendConsentDialogResult}
            title={RESEND_DIALOG_TITLE}
            message={RESEND_DIALOG_MESSAGE}
          />
        </Container>
      </Fade>
    );
  }
}

const mapStateToProps = (state: IAppState): IStateProps => ({
  loadState: state.publishSettings.loadState,
  isWorking: state.publishSettings.isWorking,
});

const mapDispatchToProps: IDispatchProps = {
  getAllSettingsActionConnect: getAllSettingsAction,
  deleteSettingsActionConnect: deleteSettingsAction,
  resendConsentActionConnect: resendPublishConsentEmailAction,
};

export default connect(mapStateToProps, mapDispatchToProps)(PublishProfilesList);
